(function () {
  'use strict';

  class NMMHeader {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.header = wrapper.querySelector('.nmm-header');
      this.triggers = Array.from(wrapper.querySelectorAll('.nmm-nav__trigger'));
      this.panels = Array.from(wrapper.querySelectorAll('.nmm-mega'));
      this.backdrop = wrapper.querySelector('.nmm-backdrop');
      this.toggle = wrapper.querySelector('.nmm-toggle');
      this.mobileNav = wrapper.querySelector('.nmm-mobile');
      this.lockToggle = wrapper.querySelector('.nmm-lock-toggle');
      this.openPanel = null;
      this.leaveTimer = null;
      this.locked = false;
      this.wrapper.classList.add('nmm--no-transition');
      this.init();
      this.initScrollShrink();
      this.publishHeaderHeight();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.wrapper.classList.remove('nmm--loading');
        this.wrapper.classList.remove('nmm--no-transition');
        this.restoreLock();
      }));
    }

    /**
     * The header is sticky, so anything an in-page anchor scrolls to lands
     * underneath it unless the target offsets itself. Its height is not a
     * constant — the contextual bar adds a row on some pages, and the mobile
     * shrink state changes it again — so publish the measured value as
     * --nmm-header-h and let anchors offset with scroll-margin-top.
     * Consumers must keep a literal fallback: this runs after first paint,
     * and a hash landing on load fires before it.
     */
    publishHeaderHeight() {
      if (!this.header) return;

      const apply = () => {
        const h = Math.round(this.header.getBoundingClientRect().height);
        if (h > 0) document.documentElement.style.setProperty('--nmm-header-h', h + 'px');
      };

      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(apply).observe(this.header);
      } else {
        window.addEventListener('resize', apply, { passive: true });
      }
      apply();
    }

    initScrollShrink() {
      if (!this.header) return;
      const mobileQuery = window.matchMedia('(max-width: 749px)');
      const threshold = 24;
      let ticking = false;

      const apply = () => {
        ticking = false;
        const shouldShrink = mobileQuery.matches && window.scrollY > threshold;
        this.header.classList.toggle('nmm-header--scrolled', shouldShrink);
      };

      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(apply);
        },
        { passive: true }
      );
      mobileQuery.addEventListener('change', apply);
      apply();
    }

    init() {
      this.triggers.forEach((trigger) => {
        const item = trigger.closest('.nmm-nav__item');
        const panelId = trigger.getAttribute('data-target');
        const panel = document.getElementById(panelId);

        item.addEventListener('mouseenter', () => {
          clearTimeout(this.leaveTimer);
          this.openMega(trigger);
        });

        item.addEventListener('mouseleave', () => {
          this.leaveTimer = setTimeout(() => this.closeAll(), 120);
        });

        if (panel) {
          panel.addEventListener('mouseenter', () => clearTimeout(this.leaveTimer));
          panel.addEventListener('mouseleave', () => {
            this.leaveTimer = setTimeout(() => this.closeAll(), 120);
          });
        }

        trigger.addEventListener('click', () => {
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!isOpen) this.openMega(trigger);
        });

        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeAll();
        });
      });

      this.backdrop?.addEventListener('click', () => {
        this.closeAll();
        this.setMobile(false);
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.openPanel) this.closeAll();
      });

      this.toggle?.addEventListener('click', () => {
        const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
        this.setMobile(!isOpen);
      });

      /* The eligibility modals open from delegated click handlers outside this
         component. Close the mobile drawer first so modal scroll locking and
         filtered backdrops never leave the drawer parked past the viewport. */
      document.addEventListener('click', (e) => {
        if (!this.isWeightCareAssessmentClick(e.target)) return;
        this.closeAll();
        /* The modal owns the scroll lock from here. Only hand it over when the
           modal is actually on the page — the trigger attribute also ships on
           templates that never render it. */
        this.setMobile(false, {
          keepScrollLock: !!(
            document.getElementById('wc-eligibility-modal') ||
            document.getElementById('nx-wc-pdp-modal')
          )
        });
      }, true);

      if (this.lockToggle) {
        if (window.Shopify && window.Shopify.designMode) {
          this.lockToggle.classList.add('is-visible');
        }
        this.lockToggle.addEventListener('click', () => this.toggleLock());
      }
    }

    toggleLock() {
      this.locked = !this.locked;
      this.wrapper.classList.toggle('nmm--locked', this.locked);
      if (this.locked && this.openPanel) {
        sessionStorage.setItem('nmm-locked-panel', this.openPanel.id);
      } else {
        sessionStorage.removeItem('nmm-locked-panel');
      }
    }

    restoreLock() {
      const panelId = sessionStorage.getItem('nmm-locked-panel');
      if (!panelId) return;
      const trigger = this.triggers.find(t => t.getAttribute('data-target') === panelId);
      if (trigger) {
        this.openMega(trigger);
        this.locked = true;
        this.wrapper.classList.add('nmm--locked');
      } else {
        sessionStorage.removeItem('nmm-locked-panel');
      }
    }

    isWeightCareAssessmentClick(target) {
      if (!target || typeof target.closest !== 'function') return false;
      if (target.closest('[data-wc-eligibility-trigger]')) return true;

      const action = target.closest('a, button');
      if (!action || !this.wrapper.contains(action)) return false;

      const href = [
        action.getAttribute('href'),
        action.getAttribute('data-href'),
        action.getAttribute('data-url')
      ].filter(Boolean).join(' ').toLowerCase();
      const text = (action.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();

      return (
        (href.includes('/pages/weightloss') && href.includes('eligibility=1')) ||
        (href.includes('/pages/nowexpress-assessment-form') && href.includes('nxformtag=nx_wl')) ||
        text.includes('assessment') ||
        text.includes('eligibility')
      );
    }

    openMega(trigger) {
      const panelId = trigger.getAttribute('data-target');
      const panel = document.getElementById(panelId);
      if (!panel) return;

      this.closeAll();
      trigger.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      this.backdrop?.classList.add('is-active');
      this.openPanel = panel;
    }

    closeAll() {
      if (this.locked) return;
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((p) => {
        p.classList.remove('is-open');
        p.setAttribute('aria-hidden', 'true');
      });
      this.backdrop?.classList.remove('is-active');
      this.openPanel = null;
    }

    setMobile(open, { keepScrollLock = false } = {}) {
      this.toggle?.setAttribute('aria-expanded', String(open));
      this.mobileNav?.setAttribute('aria-hidden', String(!open));
      this.mobileNav?.classList.toggle('is-open', open);
      this.wrapper.classList.toggle('nmm--mobile-open', open);
      if (open) {
        document.body.style.overflow = 'hidden';
      } else if (!keepScrollLock) {
        document.body.style.overflow = '';
      }
      if (this.wrapper.classList.contains('nmm--drawer-mode')) {
        this.backdrop?.classList.toggle('is-active', open);
      }
    }
  }

  class NMMSearch {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.btn = wrapper.querySelector('.nmm-search-btn');
      this.drawer = wrapper.querySelector('.nmm-search-drawer');
      this.input = this.drawer?.querySelector('.nmm-search-drawer__input');
      this.results = this.drawer?.querySelector('.nmm-search-drawer__results');
      this.closeBtn = this.drawer?.querySelector('.nmm-search-drawer__close');
      this.backdrop = wrapper.querySelector('.nmm-backdrop');
      this.debounceTimer = null;
      this.lastQuery = '';
      if (this.btn && this.drawer) this.init();
    }

    init() {
      this.btn.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());

      this.input.addEventListener('input', () => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.suggest(), 250);
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    open() {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.btn.setAttribute('aria-expanded', 'true');
      this.backdrop?.classList.add('is-active');
      requestAnimationFrame(() => this.input.focus());
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.btn.setAttribute('aria-expanded', 'false');
      this.backdrop?.classList.remove('is-active');
    }

    async suggest() {
      const q = this.input.value.trim();
      if (q === this.lastQuery) return;
      this.lastQuery = q;

      if (q.length < 2) {
        this.results.innerHTML = '';
        return;
      }

      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,article,page&resources[limit]=5`;
      try {
        const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        this.render(q, data.resources.results);
      } catch (_) {}
    }

    render(q, results) {
      const { products = [], articles = [], pages = [] } = results;
      const total = products.length + articles.length + pages.length;

      if (total === 0) {
        this.results.innerHTML = `<p class="nmm-search-results__empty">No results for "<strong>${this.escape(q)}</strong>"</p>`;
        return;
      }

      let html = '';

      if (products.length) {
        html += `<p class="nmm-search-results__group-label">Products</p><ul class="nmm-search-results__list">`;
        products.forEach(p => {
          const img = p.image
            ? `<img class="nmm-search-results__thumb" src="${p.image}" alt="${this.escape(p.title)}" width="44" height="44" loading="lazy">`
            : `<div class="nmm-search-results__thumb-placeholder"></div>`;
          const price = p.price ? `<div class="nmm-search-results__price">${p.price}</div>` : '';
          html += `<li class="nmm-search-results__item"><a href="${p.url}">${img}<div class="nmm-search-results__text"><div class="nmm-search-results__title">${this.escape(p.title)}</div>${price}</div></a></li>`;
        });
        html += `</ul>`;
      }

      if (articles.length) {
        html += `<p class="nmm-search-results__group-label">Guides</p><ul class="nmm-search-results__list">`;
        articles.forEach(a => {
          const img = a.image
            ? `<img class="nmm-search-results__thumb" src="${a.image}" alt="${this.escape(a.title)}" width="44" height="44" loading="lazy">`
            : `<div class="nmm-search-results__thumb-placeholder"></div>`;
          html += `<li class="nmm-search-results__item"><a href="${a.url}">${img}<div class="nmm-search-results__text"><div class="nmm-search-results__title">${this.escape(a.title)}</div></div></a></li>`;
        });
        html += `</ul>`;
      }

      if (pages.length) {
        html += `<p class="nmm-search-results__group-label">Pages</p><ul class="nmm-search-results__list">`;
        pages.forEach(pg => {
          html += `<li class="nmm-search-results__item"><a href="${pg.url}"><div class="nmm-search-results__thumb-placeholder"></div><div class="nmm-search-results__text"><div class="nmm-search-results__title">${this.escape(pg.title)}</div></div></a></li>`;
        });
        html += `</ul>`;
      }

      html += `<a class="nmm-search-results__view-all" href="/search?q=${encodeURIComponent(q)}&type=product,article,page">See all results for "${this.escape(q)}"</a>`;

      this.results.innerHTML = html;
    }

    escape(str) {
      return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }
  }

  class NMMSearchInline {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.container = wrapper.querySelector('.nmm-search-inline');
      this.input = this.container?.querySelector('.nmm-search-inline__input');
      this.results = this.container?.querySelector('.nmm-search-inline__results');
      this.locBtn = this.container?.querySelector('.nmm-search-inline__loc-btn');
      this.locLabel = this.container?.querySelector('.nmm-search-inline__loc-label');
      this.locDropdown = this.container?.querySelector('.nmm-search-inline__loc-dropdown');
      this.debounceTimer = null;
      this.lastQuery = '';
      if (this.input && this.results) this.init();
    }

    init() {
      this.input.addEventListener('input', () => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.suggest(), 250);
      });

      document.addEventListener('click', (e) => {
        if (!this.container.contains(e.target)) {
          this.results.innerHTML = '';
          this.closeLocDropdown();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { this.results.innerHTML = ''; this.closeLocDropdown(); }
      });

      if (this.locBtn && this.locDropdown) this.initLocation();
    }

    /* ---------- Location ---------- */

    parseGroups(raw) {
      const lines = raw.split('\n');
      const groups = [];
      let cur = null;
      for (const line of lines) {
        const t = line.trim();
        if (!t) continue;
        if (t.startsWith('[') && t.endsWith(']')) {
          cur = { label: t.slice(1, -1), items: [] };
          groups.push(cur);
        } else {
          if (!cur) { cur = { label: '', items: [] }; groups.push(cur); }
          cur.items.push(t);
        }
      }
      return groups;
    }

    initLocation() {
      const saved = localStorage.getItem('nmm-location');
      const def = this.container.dataset.locationDefault || '';
      const title = this.container.dataset.locTitle || 'Choose testing location';
      const desc = this.container.dataset.locDesc || '';
      let raw = '';
      try { raw = JSON.parse(this.container.dataset.locations || '""'); } catch (_) {}
      const groups = this.parseGroups(raw);
      const current = saved || def;

      if (current) {
        this.setLocationLabel(current);
        const t = this.container.querySelector('.nmm-loc-tag-filter');
        if (t) t.value = current;
      }

      this.buildLocDropdown(groups, current, title, desc);

      this.locBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.locDropdown.classList.contains('is-open') ? this.closeLocDropdown() : this.openLocDropdown();
      });

      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then(p => {
          if (p.state === 'granted') this.detectLocation(true);
        }).catch(() => {});
      }
    }

    buildLocDropdown(groups, current, title, desc) {
      const CHECK = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;

      let groupsHtml = '';
      for (const g of groups) {
        const label = g.label ? `<p class="nmm-loc-group__label">${this.escape(g.label)}</p>` : '';
        const items = g.items.map(item => {
          const sel = item === current;
          return `<li class="nmm-loc-item${sel ? ' nmm-loc-item--selected' : ''}" role="option" aria-selected="${sel}" data-value="${this.escape(item)}">
            <span class="nmm-loc-item__check">${sel ? CHECK : ''}</span>${this.escape(item)}</li>`;
        }).join('');
        groupsHtml += `<div class="nmm-loc-group">${label}<ul style="list-style:none;margin:0;padding:0">${items}</ul></div>`;
      }

      this.locDropdown.innerHTML = `
        <div class="nmm-loc-header">
          <p class="nmm-loc-header__title">${this.escape(title)}</p>
          ${desc ? `<p class="nmm-loc-header__desc">${this.escape(desc)}</p>` : ''}
        </div>
        <button type="button" class="nmm-loc-detect-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>
          <span class="nmm-loc-detect-btn__label">Use my current location</span>
        </button>
        <div class="nmm-loc-search-wrap">
          <input type="text" class="nmm-loc-search-input" placeholder="Search city or area" autocomplete="off">
        </div>
        <div class="nmm-loc-groups">${groupsHtml}</div>`;

      this.locDropdown.querySelector('.nmm-loc-detect-btn').addEventListener('click', () => this.detectLocation());

      const searchInput = this.locDropdown.querySelector('.nmm-loc-search-input');
      searchInput.addEventListener('input', () => {
        const q = searchInput.value.toLowerCase();
        this.locDropdown.querySelectorAll('.nmm-loc-item').forEach(li => {
          li.style.display = li.dataset.value.toLowerCase().includes(q) ? '' : 'none';
        });
        this.locDropdown.querySelectorAll('.nmm-loc-group').forEach(g => {
          const any = [...g.querySelectorAll('.nmm-loc-item')].some(li => li.style.display !== 'none');
          g.style.display = any ? '' : 'none';
        });
      });

      this.locDropdown.querySelectorAll('.nmm-loc-item').forEach(li => {
        li.addEventListener('click', () => { this.setLocation(li.dataset.value); this.closeLocDropdown(); });
      });
    }

    setLocationLabel(name) {
      if (this.locLabel) this.locLabel.textContent = name;
    }

    setLocation(name) {
      const CHECK = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;
      localStorage.setItem('nmm-location', name);
      this.setLocationLabel(name);
      this.locDropdown?.querySelectorAll('.nmm-loc-item').forEach(li => {
        const sel = li.dataset.value === name;
        li.classList.toggle('nmm-loc-item--selected', sel);
        li.setAttribute('aria-selected', String(sel));
        const chk = li.querySelector('.nmm-loc-item__check');
        if (chk) chk.innerHTML = sel ? CHECK : '';
      });
      const t = this.container.querySelector('.nmm-loc-tag-filter');
      if (t) t.value = name;
    }

    openLocDropdown() {
      this.locDropdown.classList.add('is-open');
      this.locDropdown.setAttribute('aria-hidden', 'false');
      this.locBtn.setAttribute('aria-expanded', 'true');
      this.results.innerHTML = '';
      setTimeout(() => this.locDropdown.querySelector('.nmm-loc-search-input')?.focus(), 50);
    }

    closeLocDropdown() {
      if (!this.locDropdown) return;
      this.locDropdown.classList.remove('is-open');
      this.locDropdown.setAttribute('aria-hidden', 'true');
      this.locBtn?.setAttribute('aria-expanded', 'false');
    }

    detectLocation(silent = false) {
      if (!navigator.geolocation) return;
      const label = this.locDropdown?.querySelector('.nmm-loc-detect-btn__label');
      if (!silent && label) label.textContent = 'Detecting…';
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude: lat, longitude: lon } = pos.coords;
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
              { headers: { 'Accept-Language': 'en' } }
            );
            if (!res.ok) throw new Error('nominatim error');
            const data = await res.json();
            const city = data.address?.city || data.address?.town || data.address?.municipality || data.address?.suburb || data.address?.county;
            if (!city) throw new Error('no city');
            const items = [...(this.locDropdown?.querySelectorAll('.nmm-loc-item') || [])];
            const matched = items.find(li => li.dataset.value.toLowerCase().includes(city.toLowerCase()));
            this.setLocation(matched ? matched.dataset.value : city);
            if (!silent && label) label.textContent = 'Use my current location';
          } catch (_) {
            if (!silent && label) label.textContent = 'Could not detect — try again';
            setTimeout(() => { if (!silent && label) label.textContent = 'Use my current location'; }, 3000);
          }
        },
        () => {
          if (!silent && label) label.textContent = 'Location access denied';
          setTimeout(() => { if (!silent && label) label.textContent = 'Use my current location'; }, 3000);
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    }

    /* ---------- Search suggest ---------- */

    async suggest() {
      const q = this.input.value.trim();
      if (q === this.lastQuery) return;
      this.lastQuery = q;
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      this.closeLocDropdown();
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,article,page&resources[limit]=5`;
      try {
        const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        this.render(q, data.resources.results);
      } catch (_) {}
    }

    render(q, results) {
      const { products = [], articles = [], pages = [] } = results;
      if (!products.length && !articles.length && !pages.length) {
        this.results.innerHTML = `<p class="nmm-search-results__empty">No results for "<strong>${this.escape(q)}</strong>"</p>`;
        return;
      }
      let html = '';
      if (products.length) {
        html += `<p class="nmm-search-results__group-label">Products</p><ul class="nmm-search-results__list">`;
        products.forEach(p => {
          const img = p.image ? `<img class="nmm-search-results__thumb" src="${p.image}" alt="${this.escape(p.title)}" width="44" height="44" loading="lazy">` : `<div class="nmm-search-results__thumb-placeholder"></div>`;
          const price = p.price ? `<div class="nmm-search-results__price">${p.price}</div>` : '';
          html += `<li class="nmm-search-results__item"><a href="${p.url}">${img}<div class="nmm-search-results__text"><div class="nmm-search-results__title">${this.escape(p.title)}</div>${price}</div></a></li>`;
        });
        html += `</ul>`;
      }
      if (articles.length) {
        html += `<p class="nmm-search-results__group-label">Guides</p><ul class="nmm-search-results__list">`;
        articles.forEach(a => {
          const img = a.image ? `<img class="nmm-search-results__thumb" src="${a.image}" alt="${this.escape(a.title)}" width="44" height="44" loading="lazy">` : `<div class="nmm-search-results__thumb-placeholder"></div>`;
          html += `<li class="nmm-search-results__item"><a href="${a.url}">${img}<div class="nmm-search-results__text"><div class="nmm-search-results__title">${this.escape(a.title)}</div></div></a></li>`;
        });
        html += `</ul>`;
      }
      if (pages.length) {
        html += `<p class="nmm-search-results__group-label">Pages</p><ul class="nmm-search-results__list">`;
        pages.forEach(pg => {
          html += `<li class="nmm-search-results__item"><a href="${pg.url}"><div class="nmm-search-results__thumb-placeholder"></div><div class="nmm-search-results__text"><div class="nmm-search-results__title">${this.escape(pg.title)}</div></div></a></li>`;
        });
        html += `</ul>`;
      }
      html += `<a class="nmm-search-results__view-all" href="/search?q=${encodeURIComponent(q)}&type=product,article,page">See all results for "${this.escape(q)}"</a>`;
      this.results.innerHTML = html;
    }

    escape(str) {
      return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }
  }

  function init() {
    document.querySelectorAll('.nmm-wrapper').forEach((w) => {
      new NMMHeader(w);
      new NMMSearch(w);
      new NMMSearchInline(w);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const wrapper = e.target.querySelector('.nmm-wrapper');
    if (wrapper) {
      new NMMHeader(wrapper);
      new NMMSearch(wrapper);
      new NMMSearchInline(wrapper);
    }
  });
})();
